import Phaser from 'phaser';
import type { WorldScale } from '../core/WorldScale.js';
import { CSS, PALETTE, UI_FONT, UI_HEAVY, drawPanel, formatEuro } from './style.js';

/** HUD copy (campaign wording from the design sketch) */
export const HUD_TEXT = {
  depot: 'ALTERSVORSORGE-\nDEPOT',
  subsidy: 'STAATLICHE\nZULAGE',
} as const;

type HudObject = Phaser.GameObjects.Graphics | Phaser.GameObjects.Text | Phaser.GameObjects.Image;

/**
 * In-game HUD that belongs to the game itself (both editions), laid out like the design
 * sketch: two navy panels at the top (own contribution in €, subsidy), below them
 * the yellow time bar that fills up over the life.
 */
export class RunHud {
  private readonly objects: HudObject[] = [];
  private readonly depotValue: Phaser.GameObjects.Text;
  private readonly subsidyValue: Phaser.GameObjects.Text;
  private readonly barFill: Phaser.GameObjects.Graphics;
  private readonly barX: number;
  private readonly barY: number;
  private readonly barW: number;
  private readonly barH: number;
  private readonly inset: number;
  private readonly unit: number;
  /** values shrink to fit their panel (narrow phones, large amounts) */
  private readonly valueMaxW: { depot: number; subsidy: number };
  private fillW = -1;
  private depot = -1;
  private subsidy = -1;

  constructor(
    private readonly scene: Phaser.Scene,
    ws: WorldScale,
    /** width of the playfield (pixels) */
    W: number,
    depth: number,
  ) {
    const px = (wu: number) => Math.round(ws.px(wu));
    this.unit = Math.max(1, px(3));
    const text = (size: number, color: string, weight = 'bold'): Phaser.Types.GameObjects.Text.TextStyle => ({
      fontFamily: UI_FONT,
      fontSize: `${px(size)}px`,
      fontStyle: weight,
      color,
    });

    // --- two panels -------------------------------------------------------------------
    const m = px(16);
    const top = px(28);
    const panelH = px(150);
    const gap = px(10);
    const pad = px(13);
    const avail = W - m * 2 - gap;
    // the left panel is wider: "ALTERSVORSORGE-" is the longest label line
    const rightW = Math.min(Math.round(avail * 0.4), px(250));
    const leftW = Math.min(avail - rightW, px(440));
    this.valueMaxW = { depot: leftW - pad * 2, subsidy: rightW - pad * 2 };
    const rightX = W - m - rightW;
    const panelStyle = { radius: px(16), border: Math.max(1, px(4)) };

    const panels = this.add(scene.add.graphics());
    drawPanel(panels, m, top, leftW, panelH, panelStyle);
    drawPanel(panels, rightX, top, rightW, panelH, panelStyle);

    this.depotValue = this.add(
      scene.add.text(m + pad, top + panelH - pad * 0.7, '0 €', text(46, CSS.text, UI_HEAVY)).setOrigin(0, 1),
    );
    this.subsidyValue = this.add(
      scene.add.text(rightX + pad, top + panelH - pad * 0.7, '0 €', text(46, CSS.bonus, UI_HEAVY)).setOrigin(0, 1),
    );

    // both labels (two lines each, see HUD_TEXT) share one font size, as large as fits both panels: their width
    // and the space between the panel top and the value
    const labelStyle = { ...text(22, CSS.label), lineSpacing: -Math.round(px(22) * 0.1) };
    const depotLabel = this.add(scene.add.text(m + pad, top + pad, HUD_TEXT.depot, labelStyle));
    const subsidyLabel = this.add(scene.add.text(rightX + pad, top + pad, HUD_TEXT.subsidy, labelStyle));
    const labelMaxH = this.depotValue.y - this.depotValue.height - px(4) - (top + pad);
    const grow = Math.min(
      this.valueMaxW.depot / Math.max(1, depotLabel.width),
      this.valueMaxW.subsidy / Math.max(1, subsidyLabel.width),
      labelMaxH / Math.max(1, Math.max(depotLabel.height, subsidyLabel.height)),
    );
    const labelSize = Math.max(1, Math.floor(px(22) * grow));
    for (const label of [depotLabel, subsidyLabel]) {
      label.setFontSize(labelSize).setLineSpacing(-Math.round(labelSize * 0.1));
    }
    // rounding of the measured sizes: never wider than the panel
    const shrink = Math.min(
      1,
      this.valueMaxW.depot / Math.max(1, depotLabel.width),
      this.valueMaxW.subsidy / Math.max(1, subsidyLabel.width),
      labelMaxH / Math.max(1, Math.max(depotLabel.height, subsidyLabel.height)),
    );
    for (const label of [depotLabel, subsidyLabel]) label.setScale(shrink);
    this.depotValue.setText('');
    this.subsidyValue.setText('');

    // --- life progress bar ----------------------------------------------------------------
    this.barX = m;
    this.barY = top + panelH + px(14);
    this.barW = W - m * 2;
    this.barH = px(32);
    this.inset = Math.max(2, px(6));
    const track = this.add(scene.add.graphics());
    drawPanel(track, this.barX, this.barY, this.barW, this.barH, { radius: this.barH / 2, border: Math.max(1, px(4)) });
    this.barFill = this.add(scene.add.graphics());

    for (const o of this.objects) o.setDepth(depth);
    this.setVisible(false);
  }

  private add<T extends HudObject>(o: T): T {
    this.objects.push(o);
    return o;
  }

  setVisible(v: boolean): void {
    for (const o of this.objects) o.setVisible(v);
  }

  /** `progress` 0…1 (time of the life) fills the bar. */
  setProgress(progress: number): void {
    const p = Math.min(1, Math.max(0, progress));
    const inner = this.barW - this.inset * 2;
    const w = Math.round(inner * p);
    if (w !== this.fillW) {
      this.fillW = w;
      this.drawFill(w);
    }
  }

  /** AV depot = own contribution (€) */
  setDepot(eur: number): void {
    if (eur === this.depot) return;
    const gained = eur > this.depot && this.depot >= 0;
    this.depot = eur;
    this.depotValue.setText(formatEuro(eur));
    fit(this.depotValue, this.valueMaxW.depot);
    if (gained) this.pop(this.depotValue, 1.12);
  }

  /** Grundzulage (€) */
  setSubsidy(eur: number): void {
    if (eur === this.subsidy) return;
    const gained = eur > this.subsidy && this.subsidy >= 0;
    this.subsidy = eur;
    this.subsidyValue.setText(formatEuro(eur));
    fit(this.subsidyValue, this.valueMaxW.subsidy);
    if (gained) this.pop(this.subsidyValue, 1.12);
  }

  private drawFill(w: number): void {
    const g = this.barFill;
    g.clear();
    if (w <= 0) return;
    const x = this.barX + this.inset;
    const y = this.barY + this.inset;
    const h = this.barH - this.inset * 2;
    const r = Math.min(h / 2, w / 2);
    g.fillStyle(PALETTE.yellowDark, 1);
    g.fillRoundedRect(x, y, w, h, r);
    g.fillStyle(PALETTE.yellow, 1);
    g.fillRoundedRect(x, y, w, h - this.unit, r);
    if (w > this.unit * 4) {
      g.fillStyle(PALETTE.yellowHi, 1);
      g.fillRect(x + r * 0.6, y + this.unit, Math.max(0, w - r * 1.2), this.unit);
    }
  }

  private pop(o: Phaser.GameObjects.Text, factor: number): void {
    const tweens = this.scene.tweens;
    // remember the resting scale once (repeated pops must not grow the object)
    tweens.killTweensOf(o);
    const base = (o.getData('baseScale') as number | undefined) ?? o.scale;
    o.setScale(base * factor);
    tweens.add({ targets: o, scale: base, duration: 220, ease: 'Quad.Out' });
  }
}

/** Scale a text down (never up) so it is at most `maxW` wide; remembers the resting scale for pops. */
function fit(t: Phaser.GameObjects.Text, maxW: number): void {
  t.scene.tweens.killTweensOf(t);
  const scale = Math.min(1, maxW / Math.max(1, t.width));
  t.setScale(scale).setData('baseScale', scale);
}
